import React, { ReactNode } from 'react';
import {
  Block,
  BLOCKS,
  Inline,
  INLINES,
  MARKS,
} from '@contentful/rich-text-types';
import {
  BOLD,
  CODE,
  H2,
  H3,
  H4,
  H5,
  H6,
  ITALIC,
  LI,
  OL,
  P,
  CalloutP,
  QUOTE,
  UL,
  UNDERLINE,
  TABLE,
} from './Renderers';
import { renderBlockEmbed } from './embeds';
import { CustomAhref } from 'components/CustomAhref/CustomAhref';
import { BlockEmbed, InlineEmbed } from 'data/SingleArticle/types';
import { Link } from 'components/Link/Link';
import { logError } from 'logging/logger';
import { GuidedRecipeShort } from 'data/Article/types';

type BlockEmbeds = (BlockEmbed | null)[];
type InlineEmbeds = (InlineEmbed | null)[];

/**
 * Base text elements from the RichText editor
 */
const baseBlockElementsConfiguration = {
  [BLOCKS.HEADING_1]: function Heading1() {
    return null;
  },
  [BLOCKS.HEADING_2]: function Heading2(
    node: Block | Inline,
    children: ReactNode,
  ) {
    return <H2>{children}</H2>;
  },
  [BLOCKS.HEADING_3]: function Heading3(
    node: Block | Inline,
    children: ReactNode,
  ) {
    return <H3>{children}</H3>;
  },
  [BLOCKS.HEADING_4]: function Heading4(
    node: Block | Inline,
    children: ReactNode,
  ) {
    return <H4>{children}</H4>;
  },
  [BLOCKS.HEADING_5]: function Heading5(
    node: Block | Inline,
    children: ReactNode,
  ) {
    return <H5>{children}</H5>;
  },
  [BLOCKS.HEADING_6]: function Heading6(
    node: Block | Inline,
    children: ReactNode,
  ) {
    return <H6>{children}</H6>;
  },
  [BLOCKS.TABLE]: function Table(node: Block | Inline, children: ReactNode) {
    const arrayOfChildren = children as ReactNode[];
    return (
      <div>
        <TABLE>
          <thead>{arrayOfChildren[0]}</thead>
          <tbody>{arrayOfChildren.slice(1)}</tbody>
        </TABLE>
      </div>
    );
  },
  [BLOCKS.PARAGRAPH]: function Paragraph(
    node: Block | Inline,
    children: ReactNode,
  ) {
    return <P>{children}</P>;
  },
  [BLOCKS.LIST_ITEM]: function ListItem(
    node: Block | Inline,
    children: ReactNode,
  ) {
    return <LI>{children}</LI>;
  },
  [BLOCKS.OL_LIST]: function OLList(node: Block | Inline, children: ReactNode) {
    return <OL>{children}</OL>;
  },
  [BLOCKS.UL_LIST]: function ULList(node: Block | Inline, children: ReactNode) {
    return <UL>{children}</UL>;
  },
  [BLOCKS.QUOTE]: function Quote(node: Block | Inline, children: ReactNode) {
    return <QUOTE>{children}</QUOTE>;
  },
};

/**
 * Contentful components, such as ZapTemplates, Callout, Tables, etc...
 */
export const renderNodeWithEmbedConfiguration = ({
  blockEmbeds,
  inlineEmbeds,
  persona,
  guidedRecipes,
}: {
  blockEmbeds: BlockEmbeds;
  inlineEmbeds: InlineEmbeds;
  persona: string | null;
  guidedRecipes?: GuidedRecipeShort[];
}) => ({
  ...baseBlockElementsConfiguration,
  [BLOCKS.EMBEDDED_ENTRY]: function EmbeddedBlockEntry(node: Block | Inline) {
    const id = node.data?.target?.sys?.id;
    const blockEmbed = blockEmbeds.find(
      (blockEmbed) => blockEmbed?.sys.id === id,
    );
    if (!blockEmbed) {
      return null;
    }
    return renderBlockEmbed(blockEmbed, persona, guidedRecipes);
  },

  [INLINES.HYPERLINK]: function Hyperlink(
    node: Block | Inline,
    children: ReactNode,
  ) {
    const href = node.data?.uri;

    if (!href) {
      return null;
    }

    return (
      <Link color="primary" href={href}>
        {children}
      </Link>
    );
  },
  // This code block handles hyperlinking to an entry, meaning to render an anchor link towards an Entry’s URL.
  [INLINES.ENTRY_HYPERLINK]: function Hyperlink(
    node: Block | Inline,
    children: ReactNode,
  ) {
    const id = node.data?.target?.sys?.id;
    const blockEmbed = blockEmbeds.find(
      (blockEmbed) => blockEmbed?.sys.id === id,
    );

    if (!blockEmbed) {
      return null;
    }

    if (blockEmbed.__typename === 'SectionAnchorAssembly') {
      const { htmlId } = blockEmbed;

      if (!htmlId) {
        throw new Error('Bad SectionAnchor');
      }

      return (
        <Link color="primary" href={`#${htmlId}`}>
          {children}
        </Link>
      );
    }
  },
  // This code block handles all embedded inline entries from the RichText article body
  [INLINES.EMBEDDED_ENTRY]: function Hyperlink(node: Block | Inline) {
    const id = node.data?.target?.sys?.id;

    const inlineEmbed = inlineEmbeds.find(
      (inlineEmbed) => inlineEmbed?.sys.id === id,
    );

    if (!inlineEmbed) {
      return null;
    }

    if (inlineEmbed.__typename === 'CustomAhrefAssembly') {
      const { destinationUrl, displayText, refTags } = inlineEmbed;

      if (!displayText || !destinationUrl) {
        logError('Bad CustomAhrefAssembly');
        return <span>Bad CustomAhrefAssembly</span>;
      }

      return (
        <CustomAhref
          destinationUrl={destinationUrl}
          displayText={displayText}
          refTags={refTags}
        />
      );
    }
  },
});

export const baseMarksElementsConfiguration = {
  [MARKS.BOLD]: function Bold(text: ReactNode) {
    return <BOLD>{text}</BOLD>;
  },
  [MARKS.CODE]: function Code(text: ReactNode) {
    return <CODE>{text}</CODE>;
  },
  [MARKS.ITALIC]: function Italic(text: ReactNode) {
    return <ITALIC>{text}</ITALIC>;
  },
  [MARKS.UNDERLINE]: function Underline(text: ReactNode) {
    return <UNDERLINE>{text}</UNDERLINE>;
  },
};

// This function overrides the p styling set in renderBaseBlockElementsConfiguration so that we can style and include semantic HTMl tags for captions
export const richImageBody = {
  ...baseBlockElementsConfiguration,
  [BLOCKS.PARAGRAPH]: function Paragraph(
    node: Block | Inline,
    children: ReactNode,
  ) {
    return <small>{children}</small>;
  },
};

export const calloutBody = {
  ...baseBlockElementsConfiguration,
  [BLOCKS.PARAGRAPH]: function Paragraph(
    node: Block | Inline,
    children: ReactNode,
  ) {
    return <CalloutP>{children}</CalloutP>;
  },
  [INLINES.HYPERLINK]: function Hyperlink(
    node: Block | Inline,
    children: ReactNode,
  ) {
    const href = node.data?.uri;

    if (!href) {
      return null;
    }

    return (
      <Link color="primary" href={href}>
        {children}
      </Link>
    );
  },
};
